/* The contact book — STORY-015.
 *
 * ── the form asks for a name and stops ──────────────────────────────────────
 * In a real guest list a display name is the only field most records have.
 * Phone and email are on the same panel and neither is required, because a form
 * that demands them gets filled with junk — which is worse than an empty field,
 * since junk looks like data.
 *
 * ── two people with one name are two people ─────────────────────────────────
 * No duplicate detection, no merge prompt, no "did you mean". Near-identical
 * names routinely belong to genuinely different people, and a false merge
 * silently destroys somebody's history. The story refuses it outright and this
 * screen does not quietly reintroduce it.
 *
 * ── the dormant count is shown, never acted on ──────────────────────────────
 * Automatic retention is off by default and is the customer's decision: they
 * are the controller and storage limitation is theirs. What this screen owes is
 * making the choice visible rather than making it for them.
 */

import { PERSON_NOTES_MAX, type Person } from '@eventuley/contracts';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextArea, TextField, UpgradePrompt } from '../../../shared/ui/index.js';
import {
  useCreatePerson,
  useDeletePerson,
  useErasePerson,
  useExportPerson,
  usePeople,
  usePerson,
  useUpdatePerson,
} from '../api/index.js';

/** The plan's own name, capitalised for a sentence — the repo's convention. */
const planLabel = (code: string) => code.charAt(0).toLocaleUpperCase('en-US') + code.slice(1);

/** A refusal the fields can explain, rather than one the callout must. */
const isFieldRefusal = (error: unknown) =>
  error instanceof ApiError && error.code === 'validation_failed';

const fieldRule = (error: unknown, path: string) =>
  error instanceof ApiError ? error.fields.find((field) => field.path === path)?.rule : undefined;

export function PeopleScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const { organisationId = '' } = useParams({ strict: false });

  const [search, setSearch] = useState('');
  const { data, error, isLoading } = usePeople(organisationId, {
    ...(search.trim() === '' ? {} : { search: search.trim() }),
  });

  const create = useCreatePerson(organisationId);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ displayName: '', phone: '', email: '', notes: '' });

  const people = data?.people ?? [];
  const total = data?.total ?? 0;
  const dormant = data?.dormant ?? 0;

  /* The cap, read from the refusal rather than from a count this screen keeps.
   * The server decides, and a client that predicted the limit would eventually
   * predict it wrongly. */
  const capped =
    create.error instanceof ApiError && create.error.code === 'entitlement_exceeded'
      ? (fieldRule(create.error, 'plan') ?? 'pro')
      : null;

  const searching = search.trim() !== '';

  const reset = () => {
    setDraft({ displayName: '', phone: '', email: '', notes: '' });
    create.reset();
    setAdding(false);
  };

  return (
    <>
      <div className="page-head">
        <h1>{t('people.title')}</h1>
        {people.length > 0 && !adding ? (
          <Button onClick={() => setAdding(true)}>{t('people.new')}</Button>
        ) : null}
      </div>

      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      {capped ? (
        <UpgradePrompt
          lead={t('people.cap.lead', { plan: planLabel(capped) })}
          limit={t('people.cap.limit')}
          alternative={t('people.cap.alternative')}
          action={t('people.cap.see_plans')}
          label={t('people.cap.lead', { plan: planLabel(capped) })}
          onAction={() => {
            void navigate({ to: '/plans', search: { workspace: organisationId } });
          }}
        />
      ) : null}

      {/* Shown whenever there is a book to search, including while a search is
          running — hiding it when a search returns nothing would trap somebody
          in an empty result with no way to clear it. */}
      {total > 0 || searching ? (
        <TextField
          label={t('people.search_label')}
          type="search"
          value={search}
          onChange={(changed) => setSearch(changed.target.value)}
        />
      ) : null}

      {adding ? (
        <section className="settings__section">
          <h2>{t('people.add_heading')}</h2>
          <form
            onSubmit={(submitted) => {
              submitted.preventDefault();
              create.mutate(
                {
                  displayName: draft.displayName,
                  /* Empty is absent, not empty-string: the contract wants a
                     value or nothing, and "" is neither. */
                  ...(draft.phone.trim() === '' ? {} : { phone: draft.phone.trim() }),
                  ...(draft.email.trim() === '' ? {} : { email: draft.email.trim() }),
                  ...(draft.notes.trim() === '' ? {} : { notes: draft.notes.trim() }),
                },
                { onSuccess: reset },
              );
            }}
            noValidate
          >
            <TextField
              label={t('people.name_label')}
              value={draft.displayName}
              onChange={(changed) => setDraft({ ...draft, displayName: changed.target.value })}
              error={
                isFieldRefusal(create.error) && fieldRule(create.error, 'phone') === undefined
                  ? t('people.name_required')
                  : undefined
              }
            />
            {/* Said out loud, because the whole point is that a name is enough. */}
            <p className="field__hint">{t('people.rest_optional')}</p>

            <TextField
              label={t('people.phone_label')}
              type="tel"
              value={draft.phone}
              onChange={(changed) => setDraft({ ...draft, phone: changed.target.value })}
              hint={t('people.phone_hint')}
              error={
                /* Two different refusals, and only one is fixable by adding a
                   country code — so they must not read the same. */
                fieldRule(create.error, 'phone') === 'needs_region'
                  ? t('people.phone_needs_region')
                  : fieldRule(create.error, 'phone') === 'invalid'
                    ? t('people.phone_invalid')
                    : undefined
              }
            />
            <TextField
              label={t('people.email_label')}
              type="email"
              value={draft.email}
              onChange={(changed) => setDraft({ ...draft, email: changed.target.value })}
            />
            {/* A textarea, because a note is a sentence and an input submits
                the form on Enter. The hint is not decoration: this field is
                where special-category data lands in practice, and saying what
                becomes of it is what makes the customer's lawful basis
                something they can act on. ADR-0017. */}
            <TextArea
              label={t('people.notes_label')}
              value={draft.notes}
              maxLength={PERSON_NOTES_MAX}
              onChange={(changed) => setDraft({ ...draft, notes: changed.target.value })}
              hint={t('people.notes_hint')}
            />

            {create.error && !isFieldRefusal(create.error) && !capped ? (
              <Callout tone="danger">
                {create.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
              </Callout>
            ) : null}

            <div className="btn-row">
              <Button type="submit" busy={create.isPending}>
                {t('people.add_submit')}
              </Button>
              <Button variant="ghost" onClick={reset}>
                {t('people.add_cancel')}
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      {isLoading ? <p className="field__hint">{t('common.loading')}</p> : null}

      {/* Nothing matched a search, which is a different statement from an empty
          contact book — and telling somebody the second when the first is true
          reads as records having disappeared. */}
      {!isLoading && people.length === 0 && searching && !error ? (
        <section className="settings__section">
          <h2>{t('people.no_match.title')}</h2>
          <p className="field__hint">{t('people.no_match.body')}</p>
          <Button onClick={() => setSearch('')}>{t('people.no_match.action')}</Button>
        </section>
      ) : null}

      {!isLoading && people.length === 0 && !searching && !adding && !error ? (
        <section className="settings__section">
          <h2>{t('people.empty.title')}</h2>
          <p className="field__hint">{t('people.empty.body')}</p>
          <Button onClick={() => setAdding(true)}>{t('people.empty.action')}</Button>
        </section>
      ) : null}

      {people.length > 0 ? (
        <>
          <ul className="row-list">
            {people.map((person) => (
              <PersonRow key={person.id} person={person} organisationId={organisationId} />
            ))}
          </ul>

          <p className="field__hint" role="status">
            {t('people.showing', { shown: String(people.length), total: String(total) })}
          </p>

          {/* Counted and shown, never acted on. The job that would delete them
              is separate, off by default, and the customer's decision — they
              are the controller and storage limitation is theirs. */}
          {dormant > 0 ? (
            <Callout tone="info">{t('people.dormant', { count: String(dormant) })}</Callout>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function PersonRow({ person, organisationId }: { person: Person; organisationId: string }) {
  const t = useTranslate();
  const update = useUpdatePerson(organisationId);
  const remove = useDeletePerson(organisationId);
  const exporting = useExportPerson(organisationId);
  const erasing = useErasePerson(organisationId);

  /* Whether the export panel is open. The erasure panel is not state: it is
   * the refusal the delete already produces, which is the flow the prototype
   * settled — Delete, "they cannot be deleted", "anonymise instead". A Member
   * asking to remove somebody is asking the same question either way, and a
   * second button for the harder answer is a second thing to find. */
  const [exportOpen, setExportOpen] = useState(false);

  const [editing, setEditing] = useState(false);

  /* The note is not in the list — a picker rendering names has no reader for
   * Article 9 free text, so it travels only when somebody opens the record.
   * ADR-0017. Fetched while the editor is open and not before. */
  const opened = usePerson(organisationId, person.id, editing);

  const [draft, setDraft] = useState({
    displayName: person.displayName,
    phone: person.phone ?? '',
    email: person.email ?? '',
    notes: '',
  });

  /**
   * Whether the note in the box is the note that is stored.
   *
   * False while the read is in flight, false if it failed, and false when the
   * stored value could not be decrypted. It gates the PATCH, and that is not
   * caution — it is the difference between saving a correction and destroying
   * a note. `draft.notes` starts empty and is filled by the read; without this
   * a single transient failure left an EMPTY, ENABLED box that looked ordinary,
   * and pressing Save wrote null over the ciphertext with nothing on screen to
   * say the note had never arrived.
   *
   * Absent is not null on this PATCH — the contract is explicit that absent
   * leaves a field alone — so the payload simply omits it.
   */
  const notesReady = opened.data !== undefined && !opened.data.notesUnreadable;

  /* The note arrives after the editor does, so the field fills in when it
   * lands. Guarded on the id as well as the value: without it, clearing the box
   * and letting a refetch settle would put the stored note back under
   * somebody's hands. */
  const [noteFor, setNoteFor] = useState<string | null>(null);
  if (editing && opened.data && noteFor !== opened.data.id) {
    setNoteFor(opened.data.id);
    setDraft((current) => ({ ...current, notes: opened.data.notes ?? '' }));
  }

  /* Opening and closing both reset the draft from the record and clear the
   * mutation. Without it a refused correction and its error survive Cancel, so
   * reopening shows the rejected value and saving repeats the same refusal. */
  const openEditor = () => {
    setDraft({
      displayName: person.displayName,
      phone: person.phone ?? '',
      email: person.email ?? '',
      notes: '',
    });
    /* Cleared, so the next open refills from a fresh read rather than showing
     * the note belonging to the last record edited. */
    setNoteFor(null);
    update.reset();
    setEditing(true);
  };

  const closeEditor = () => {
    openEditor();
    setEditing(false);
  };

  const onGuestList =
    remove.error instanceof ApiError && remove.error.code === 'conflict'
      ? Number(fieldRule(remove.error, 'attendanceCount') ?? 0)
      : null;

  return (
    <li className="row">
      <span className="row__main">
        {/* An erased Person's name is empty in the database — ADR-0015 stores
            no label, so the one fixed label is rendered here and a Turkish
            reader sees it in Turkish. */}
        <span className="row__title">
          {person.anonymised ? t('people.erased_label') : person.displayName}
        </span>
        {/* Both shown when present and neither invented when absent — a dash
            where a phone number would be reads as a number nobody has. */}
        {person.phone ? <span className="row__meta">{person.phone}</span> : null}
        {person.email ? <span className="row__meta">{person.email}</span> : null}
        {/* That one exists, never what it says. The note is Article 9 free text
            and the list does not carry it — but without this marker a saved
            note is invisible until somebody opens each row in turn. */}
        {person.hasNotes ? <span className="row__meta">{t('people.has_note')}</span> : null}
      </span>

      <span className="row__actions">
        <Button variant="ghost" onClick={editing ? closeEditor : openEditor}>
          {t('people.edit')}
        </Button>
        {/* Both hidden once the subject is severed: there is nothing left to
            export and nothing left to erase. Delete was still offered, and on
            an erased Person who keeps their Attendances it answered with the
            same 409 — reopening a panel headed "Erase ?", because the name it
            interpolates is empty by then, and offering to erase somebody
            already erased.

            Edit stays. Rectification is a right too, and a record with no
            subject can still carry a correction to what it does hold. */}
        {person.anonymised ? null : (
          <>
            <Button
              variant="ghost"
              busy={remove.isPending}
              onClick={() => remove.mutate(person.id)}
            >
              {t('people.delete')}
            </Button>
            <Button
              variant="ghost"
              busy={exporting.isPending}
              onClick={() => {
                exporting.reset();
                setExportOpen(true);
                exporting.mutate(person.id);
              }}
            >
              {t('people.export')}
            </Button>
          </>
        )}
      </span>

      {exportOpen ? (
        <div className="row__expand">
          <Callout tone="info">
            <strong>{t('people.export.title', { name: person.displayName })}</strong>
            {/* Says what the file holds BEFORE producing it, naming the
                attendances specifically: they are the part an organiser
                forgets is personal data and the part the guest most often
                means. */}
            <p>
              {exporting.data
                ? t('people.export.body', { count: exporting.data.attendances.length })
                : t('common.loading')}
            </p>
            <p>{t('people.free_text_caveat')}</p>
            {exporting.error ? <p>{t('people.export.failed')}</p> : null}
            {exporting.data ? (
              <pre className="account__export">{JSON.stringify(exporting.data, null, 2)}</pre>
            ) : null}
            <div className="btn-row">
              <Button variant="ghost" onClick={() => setExportOpen(false)}>
                {t('people.export.close')}
              </Button>
            </div>
          </Callout>
        </div>
      ) : null}

      {onGuestList !== null ? (
        <div className="row__expand">
          <Callout tone="danger">
            <strong>{t('people.erase.title', { name: person.displayName })}</strong>
            {/* Why the delete was refused, before what to do instead. */}
            <p>{t('people.on_guest_list', { count: String(onGuestList) })}</p>
            {/* Names what SURVIVES as well as what does not. An irreversible
                action described only by what it destroys is one nobody can
                weigh — and the count is what decides which of the two things
                this does. */}
            <p>{t('people.erase.body', { count: onGuestList ?? 0 })}</p>
            <p>{t('people.erase.irreversible')}</p>
            <p>{t('people.free_text_caveat')}</p>
            {erasing.error ? <p>{t('people.erase.failed')}</p> : null}
            <div className="btn-row">
              <Button
                variant="secondary"
                busy={erasing.isPending}
                onClick={() => erasing.mutate(person.id, { onSuccess: () => remove.reset() })}
              >
                {t('people.erase.submit')}
              </Button>
              <Button variant="ghost" onClick={() => remove.reset()}>
                {t('people.erase.cancel')}
              </Button>
            </div>
          </Callout>
        </div>
      ) : null}

      {/* An irreversible action that changes the row under you and says
          nothing is one nobody can be sure worked. A live region, because for
          a deleted Person the row itself disappears. */}
      {erasing.isSuccess && erasing.data ? (
        <div className="row__expand">
          <Callout tone="info">
            {erasing.data.outcome === 'deleted'
              ? t('people.erase.done_deleted')
              : t('people.erase.done_anonymised', { count: erasing.data.attendancesKept })}
          </Callout>
        </div>
      ) : null}

      {editing ? (
        <form
          className="row__expand"
          onSubmit={(submitted) => {
            submitted.preventDefault();
            update.mutate(
              {
                personId: person.id,
                displayName: draft.displayName,
                /* Empty clears; absent would leave it alone. On this form every
                   field is on screen, so an empty box is a deliberate clear. */
                phone: draft.phone.trim() === '' ? null : draft.phone.trim(),
                email: draft.email.trim() === '' ? null : draft.email.trim(),
                /* Omitted unless the box holds what is stored. A note that
                   could not be read is still a note, and it decrypts again the
                   moment a dropped key is put back — writing null over it now
                   would throw away the only copy. */
                ...(notesReady
                  ? { notes: draft.notes.trim() === '' ? null : draft.notes.trim() }
                  : {}),
              },
              { onSuccess: () => setEditing(false) },
            );
          }}
          noValidate
        >
          <TextField
            label={t('people.name_label')}
            value={draft.displayName}
            onChange={(changed) => setDraft({ ...draft, displayName: changed.target.value })}
          />
          <TextField
            label={t('people.phone_label')}
            type="tel"
            value={draft.phone}
            onChange={(changed) => setDraft({ ...draft, phone: changed.target.value })}
            error={
              fieldRule(update.error, 'phone') === 'needs_region'
                ? t('people.phone_needs_region')
                : fieldRule(update.error, 'phone') === 'invalid'
                  ? t('people.phone_invalid')
                  : undefined
            }
          />
          <TextField
            label={t('people.email_label')}
            type="email"
            value={draft.email}
            onChange={(changed) => setDraft({ ...draft, email: changed.target.value })}
          />
          <TextArea
            label={t('people.notes_label')}
            value={draft.notes}
            maxLength={PERSON_NOTES_MAX}
            /* Not editable until it is known to be safe: the hint says to
               leave an unreadable note alone, and a field that invites typing
               contradicts it. */
            disabled={person.hasNotes && !notesReady}
            onChange={(changed) => setDraft({ ...draft, notes: changed.target.value })}
            hint={
              /* Never blank on a failure. A note rendered as nothing looks
                 exactly like a note nobody wrote, and saving over it would
                 destroy what could not be read. */
              opened.data?.notesUnreadable
                ? t('people.notes_unreadable')
                : opened.error
                  ? t('people.notes_load_failed')
                  : person.hasNotes && opened.isLoading
                    ? t('common.loading')
                    : t('people.notes_hint')
            }
          />

          <div className="btn-row">
            <Button type="submit" busy={update.isPending}>
              {t('people.save')}
            </Button>
            <Button variant="ghost" onClick={closeEditor}>
              {t('people.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
